import { useState } from 'react';
import { ChartFrame } from './ChartFrame';
import { arcPath, arcsFor } from './geometry';
import { markerIsStroke, markerPath, seriesColor, seriesHatch, seriesMarker } from './series';
import type { Datum, Formatter } from './types';

interface DonutChartProps {
  title: string;
  summary?: string | undefined;
  points: readonly Datum[];
  /**
   * The figure in the hole.
   *
   * A pre-formatted string, and never a bare zero — pass the `zero_label` the
   * server supplies, the same rule `StatTile` enforces. "0" in the middle of an
   * empty ring reads as a failure; "No visits yet" reads as a fact.
   */
  centre: { value: string; label: string };
  format?: Formatter | undefined;
  size?: number | undefined;
  onSelect?: ((key: string) => void) | undefined;
}

/**
 * Share of a whole, for a handful of categories.
 *
 * A donut and not a pie, because the hole is useful: it carries the total,
 * which is the number a reader wants first and which a pie has nowhere to put.
 *
 * Single-series by construction. A donut of donuts is a chart nobody can read,
 * and the shape that answers "share, over time" is a stacked bar.
 */
export function DonutChart({
  title,
  summary,
  points,
  centre,
  format = String,
  size = 200,
  onSelect,
}: DonutChartProps) {
  const [activeKey, setActiveKey] = useState<string | undefined>();

  const arcs = arcsFor(points);
  const series = [{ id: 'share', label: title, points }];

  return (
    <ChartFrame
      title={title}
      summary={summary}
      series={series}
      height={size}
      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
      format={format}
      legend={false}
      categoryLabel="Category"
    >
      {(plot) => {
        const radius = Math.min(plot.inner.width, plot.inner.height) / 2;
        const outer = Math.max(0, radius - 2);
        // A ring thinner than about a fifth of the radius stops reading as a
        // proportion and starts reading as an outline.
        const inner = outer * 0.62;

        return (
          <g transform={`translate(${plot.inner.width / 2} ${plot.inner.height / 2})`}>
            {arcs.map((arc) => {
              const hatch = seriesHatch(arc.index);
              const dimmed = activeKey !== undefined && activeKey !== arc.datum.key;
              return (
                <g
                  key={arc.datum.key}
                  aria-hidden="true"
                  opacity={dimmed ? 0.35 : 1}
                  onPointerEnter={() => setActiveKey(arc.datum.key)}
                  onPointerLeave={() => setActiveKey(undefined)}
                  onClick={onSelect ? () => onSelect(arc.datum.key) : undefined}
                  style={onSelect ? { cursor: 'pointer' } : undefined}
                >
                  <path
                    className="c-chart__arc"
                    d={arcPath(arc, outer, inner)}
                    fill={seriesColor(arc.index)}
                  />
                  {hatch && <path d={arcPath(arc, outer, inner)} fill={`url(#${hatch})`} />}
                </g>
              );
            })}

            <text className="c-chart__centre-value tnum" textAnchor="middle" dy="-0.1em">
              {centre.value}
            </text>
            <text className="c-chart__centre-label" textAnchor="middle" dy="1.4em">
              {centre.label}
            </text>
          </g>
        );
      }}
    </ChartFrame>
  );
}

/**
 * The donut's legend, with each slice's share.
 *
 * Separate from `Legend` because a donut's entries carry numbers — the slices
 * are too thin to label in place, and a leader line is not worth building for a
 * chart that already has a table underneath it.
 */
export function DonutLegend({
  points,
  format = String,
}: {
  points: readonly Datum[];
  format?: Formatter;
}) {
  const arcs = arcsFor(points);
  if (arcs.length === 0) return null;

  return (
    <ul className="c-chart-legend c-chart-legend--stacked">
      {arcs.map((arc) => {
        const marker = seriesMarker(arc.index);
        const stroked = markerIsStroke(marker);
        const colour = seriesColor(arc.index);

        return (
          <li key={arc.datum.key} className="c-chart-legend__item">
            <svg className="c-chart-legend__swatch" viewBox="-6 -6 12 12" aria-hidden="true">
              <path
                d={markerPath(marker, 9)}
                fill={stroked ? 'none' : colour}
                stroke={colour}
                strokeWidth={stroked ? 2 : 0}
              />
            </svg>
            <span className="c-chart-legend__label">{arc.datum.label}</span>
            <span className="c-chart-legend__value tnum">
              {format(arc.datum.value ?? 0)} · {Math.round(arc.share * 100)}%
            </span>
          </li>
        );
      })}
    </ul>
  );
}
